/**
 * Stats page — listening history aggregated over selectable time windows.
 */
import { useState } from "react";
import type { StatsPeriod } from "@memes/shared";
import { OwnerBadge } from "../components/OwnerBadge";
import { fmtDuration } from "../lib/format";
import { useStats } from "../hooks/useStats";

const PERIODS: { value: StatsPeriod; label: string }[] = [
  { value: "1w", label: "1 week" },
  { value: "1m", label: "1 month" },
  { value: "3m", label: "3 months" },
  { value: "6m", label: "6 months" },
  { value: "1y", label: "1 year" },
  { value: "ytd", label: "Year to date" },
  { value: "all", label: "All time" },
];

export const Stats: React.FC = () => {
  const [period, setPeriod] = useState<StatsPeriod>("1m");
  const { data, isLoading, error } = useStats(period);

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Listening stats</h1>
          <p className="text-sm text-gray-500">
            Plays count only after ~30s or half the track (whichever is less). Listening time
            uses actual seconds heard.
          </p>
        </div>
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value as StatsPeriod)}
          className="rounded-xl border px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-700"
        >
          {PERIODS.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
        </select>
      </header>

      {isLoading && <p className="text-sm">Loading…</p>}
      {error && <p className="text-sm text-red-500">Failed to load stats.</p>}

      {data && (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="card p-4">
              <p className="text-xs uppercase tracking-wider text-gray-500">Total listening</p>
              <p className="mt-1 text-2xl font-bold">
                {fmtDuration(data.totalListeningSeconds)}
              </p>
              <p className="text-xs text-gray-500">
                {data.totalListeningHours} h · {data.totalListeningMinutes} min
              </p>
            </div>
            <div className="card p-4">
              <p className="text-xs uppercase tracking-wider text-gray-500">Plays</p>
              <p className="mt-1 text-2xl font-bold">{data.totalPlayCount}</p>
            </div>
            <div className="card p-4">
              <p className="text-xs uppercase tracking-wider text-gray-500">Owners tracked</p>
              <p className="mt-1 text-2xl font-bold">{data.ownerBreakdown.length}</p>
            </div>
          </div>

          <section className="card p-4">
            <h2 className="mb-3 text-sm font-semibold">Top tracks</h2>
            {data.topTracks.length === 0 ? (
              <p className="text-xs text-gray-500">No plays in this window.</p>
            ) : (
              <ol className="space-y-1 text-sm">
                {data.topTracks.map((t, i) => (
                  <li
                    key={`${t.trackId}-${i}`}
                    className="flex items-center justify-between gap-3"
                  >
                    <span className="truncate">
                      <span className="w-6 inline-block text-gray-400">{i + 1}.</span>{" "}
                      {t.title ?? "(untitled)"}
                      {t.artist ? ` — ${t.artist}` : ""}
                    </span>
                    <span className="text-xs text-gray-500">
                      {t.playCount} plays · {fmtDuration(t.listenedSeconds)}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </section>

          <section className="card p-4">
            <h2 className="mb-3 text-sm font-semibold">Top artists</h2>
            {data.topArtists.length === 0 ? (
              <p className="text-xs text-gray-500">No plays in this window.</p>
            ) : (
              <ol className="space-y-1 text-sm">
                {data.topArtists.map((a, i) => (
                  <li
                    key={`${a.artist}-${i}`}
                    className="flex items-center justify-between gap-3"
                  >
                    <span>
                      <span className="w-6 inline-block text-gray-400">{i + 1}.</span>{" "}
                      {a.artist}
                    </span>
                    <span className="text-xs text-gray-500">
                      {a.playCount} plays · {fmtDuration(a.listenedSeconds)}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </section>

          <section className="card p-4">
            <h2 className="mb-3 text-sm font-semibold">Per-owner breakdown</h2>
            <ul className="space-y-1 text-sm">
              {data.ownerBreakdown.map((o) => (
                <li key={o.owner} className="flex items-center justify-between gap-3">
                  <OwnerBadge owner={o.owner} />
                  <span className="text-xs text-gray-500">
                    {o.playCount} plays · {fmtDuration(o.listenedSeconds)}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        </>
      )}
    </div>
  );
};
